/**
 * FAQ Import/Export Component
 * 
 * Provides functionality to:
 * - Export FAQs to JSON file with filter options
 * - Import FAQs from JSON file
 * - Validate import data before applying
 * - Download sample import template
 * 
 * System Admin only feature for bulk FAQ management.
 */

import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Tabs,
  Tab,
  FormControlLabel,
  Checkbox,
  Alert,
  AlertTitle,
  Stack,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  CircularProgress,
  LinearProgress,
  Paper,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Upload as UploadIcon,
  CloudDownload as ExportIcon,
  CloudUpload as ImportIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Description as FileIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Category as CategoryIcon,
  Help as FaqIcon,
} from '@mui/icons-material';

import CustomButton from '../../../components/@extended/CustomButton';
import {
  useLazyExportFaqsQuery,
  useImportFaqsMutation,
  useValidateFaqImportMutation,
  useLazyGetFaqImportTemplateQuery,
} from '../../../store/slices/apiSlice';

// Tab Panel Component
const TabPanel = ({ children, value, index, ...props }) => (
  <Box
    role="tabpanel"
    hidden={value !== index}
    id={`faq-import-export-tabpanel-${index}`}
    aria-labelledby={`faq-import-export-tab-${index}`}
    {...props}
  >
    {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
  </Box>
);

// Export Tab Content
const ExportTab = ({ onClose }) => {
  const [exportOptions, setExportOptions] = useState({
    activeOnly: false,
    publishedOnly: false,
  });
  const [exportTrigger, { isLoading, isFetching }] = useLazyExportFaqsQuery();

  const handleExport = async () => {
    try {
      const result = await exportTrigger(exportOptions).unwrap();
      
      // Create and download JSON file
      const blob = new Blob([JSON.stringify(result, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `faqs-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Export all FAQ categories and their FAQs to a JSON file. This file can be used for backup or to import FAQs into another system.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Export Options
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={exportOptions.activeOnly}
              onChange={(e) => setExportOptions(prev => ({ ...prev, activeOnly: e.target.checked }))}
            />
          }
          label="Export only active categories"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={exportOptions.publishedOnly}
              onChange={(e) => setExportOptions(prev => ({ ...prev, publishedOnly: e.target.checked }))}
            />
          }
          label="Export only published FAQs"
        />
      </Paper>

      <Stack direction="row" spacing={2} justifyContent="flex-end">
        <CustomButton variant="outlined" onClick={onClose}>
          Cancel
        </CustomButton>
        <CustomButton
          variant="contained"
          startIcon={isLoading || isFetching ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
          onClick={handleExport}
          disabled={isLoading || isFetching}
        >
          {isLoading || isFetching ? 'Exporting...' : 'Export FAQs'}
        </CustomButton>
      </Stack>
    </Box>
  );
};

// Import Tab Content
const ImportTab = ({ onClose, onSuccess }) => {
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [importData, setImportData] = useState(null);
  const [importOptions, setImportOptions] = useState({
    updateExisting: false,
    mergeFaqs: true,
  });
  const [validationResult, setValidationResult] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [parseError, setParseError] = useState(null);

  const [validateImport, { isLoading: isValidating }] = useValidateFaqImportMutation();
  const [importFaqs, { isLoading: isImporting }] = useImportFaqsMutation();
  const [getTemplate, { isLoading: isLoadingTemplate }] = useLazyGetFaqImportTemplateQuery();

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setValidationResult(null);
      setImportResult(null);
      setParseError(null);

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const json = JSON.parse(e.target.result);
          setImportData(json);
          setParseError(null);
        } catch (err) {
          setParseError('Invalid JSON file. Please check the file format.');
          setImportData(null);
        }
      };
      reader.readAsText(file);
    }
  };

  const handleValidate = async () => {
    if (!importData) return;

    try {
      const payload = {
        categories: importData.categories || [],
        updateExisting: importOptions.updateExisting,
        mergeFaqs: importOptions.mergeFaqs,
      };
      const result = await validateImport(payload).unwrap();
      setValidationResult(result);
    } catch (error) {
      setValidationResult({
        valid: false,
        errors: [error.data?.message || 'Validation failed'],
        preview: null,
      });
    }
  };

  const handleImport = async () => {
    if (!importData) return;

    try {
      const payload = {
        categories: importData.categories || [],
        updateExisting: importOptions.updateExisting,
        mergeFaqs: importOptions.mergeFaqs,
      };
      const result = await importFaqs(payload).unwrap();
      setImportResult(result);
      if (result.success) {
        onSuccess?.();
      }
    } catch (error) {
      setImportResult({
        success: false,
        message: error.data?.message || 'Import failed',
        errors: [error.data?.message || 'An error occurred during import'],
      });
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const result = await getTemplate().unwrap();
      
      const blob = new Blob([JSON.stringify(result, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'faq-import-template.json';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download template:', error);
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    setImportData(null);
    setValidationResult(null);
    setImportResult(null);
    setParseError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Render import success/result
  if (importResult) {
    return (
      <Box>
        <Alert 
          severity={importResult.success ? 'success' : 'error'} 
          sx={{ mb: 3 }}
          icon={importResult.success ? <SuccessIcon /> : <ErrorIcon />}
        >
          <AlertTitle>{importResult.success ? 'Import Successful' : 'Import Failed'}</AlertTitle>
          {importResult.message}
        </Alert>

        {importResult.success && (
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Import Summary
            </Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
              <Chip 
                icon={<CategoryIcon />} 
                label={`${importResult.categoriesCreated} categories created`} 
                color="success" 
                variant="outlined" 
                size="small"
              />
              <Chip 
                icon={<CategoryIcon />} 
                label={`${importResult.categoriesUpdated} categories updated`} 
                color="info" 
                variant="outlined" 
                size="small"
              />
              <Chip 
                icon={<FaqIcon />} 
                label={`${importResult.faqsCreated} FAQs created`} 
                color="success" 
                variant="outlined" 
                size="small"
              />
              <Chip 
                icon={<FaqIcon />} 
                label={`${importResult.faqsUpdated} FAQs updated`} 
                color="info" 
                variant="outlined" 
                size="small"
              />
            </Stack>
          </Paper>
        )}

        {importResult.errors?.length > 0 && (
          <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'error.lighter' }}>
            <Typography variant="subtitle2" color="error" gutterBottom>
              Errors ({importResult.errors.length})
            </Typography>
            <List dense>
              {importResult.errors.map((error, index) => (
                <ListItem key={index} sx={{ py: 0 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <ErrorIcon color="error" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText 
                    primary={error} 
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        )}

        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <CustomButton variant="outlined" onClick={handleReset} startIcon={<RefreshIcon />}>
            Import Another
          </CustomButton>
          <CustomButton variant="contained" onClick={onClose}>
            Done
          </CustomButton>
        </Stack>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Import FAQ categories and FAQs from a JSON file. You can download a template to see the required format.
      </Typography>

      <CustomButton
        variant="text"
        size="small"
        startIcon={isLoadingTemplate ? <CircularProgress size={16} /> : <FileIcon />}
        onClick={handleDownloadTemplate}
        disabled={isLoadingTemplate}
        sx={{ mb: 3 }}
      >
        Download Import Template
      </CustomButton>

      {/* File Upload */}
      <Paper 
        variant="outlined" 
        sx={{ 
          p: 3, 
          mb: 3, 
          textAlign: 'center',
          border: '2px dashed',
          borderColor: selectedFile ? 'primary.main' : 'grey.300',
          bgcolor: selectedFile ? 'primary.lighter' : 'grey.50',
          cursor: 'pointer',
          '&:hover': {
            borderColor: 'primary.main',
            bgcolor: 'primary.lighter',
          },
        }}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".json"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
        {selectedFile ? (
          <Box>
            <FileIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
            <Typography variant="subtitle1" fontWeight={600}>
              {selectedFile.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {(selectedFile.size / 1024).toFixed(2)} KB
            </Typography>
          </Box>
        ) : (
          <Box>
            <UploadIcon sx={{ fontSize: 40, color: 'grey.400', mb: 1 }} />
            <Typography variant="subtitle1" color="text.secondary">
              Click to select a JSON file
            </Typography>
            <Typography variant="caption" color="text.secondary">
              or drag and drop
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Parse Error */}
      {parseError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {parseError}
        </Alert>
      )}

      {/* Import Data Preview */}
      {importData && !parseError && (
        <>
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              File Contents
            </Typography>
            <Stack direction="row" spacing={2}>
              <Chip 
                icon={<CategoryIcon />} 
                label={`${importData.categories?.length || 0} categories`} 
                variant="outlined" 
                size="small"
              />
              <Chip 
                icon={<FaqIcon />} 
                label={`${importData.categories?.reduce((sum, cat) => sum + (cat.faqs?.length || 0), 0) || 0} FAQs`} 
                variant="outlined" 
                size="small"
              />
            </Stack>
          </Paper>

          {/* Import Options */}
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Import Options
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={importOptions.updateExisting}
                  onChange={(e) => setImportOptions(prev => ({ ...prev, updateExisting: e.target.checked }))}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">Update existing categories</Typography>
                  <Typography variant="caption" color="text.secondary">
                    If unchecked, existing categories with the same name will be skipped
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={importOptions.mergeFaqs}
                  onChange={(e) => setImportOptions(prev => ({ ...prev, mergeFaqs: e.target.checked }))}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">Merge FAQs</Typography>
                  <Typography variant="caption" color="text.secondary">
                    If unchecked, existing FAQs in updated categories will be replaced
                  </Typography>
                </Box>
              }
            />
          </Paper>

          {/* Validation Result */}
          {validationResult && (
            <Alert 
              severity={validationResult.valid ? 'success' : 'warning'} 
              sx={{ mb: 3 }}
              icon={validationResult.valid ? <SuccessIcon /> : <WarningIcon />}
            >
              <AlertTitle>
                {validationResult.valid ? 'Validation Passed' : 'Validation Issues Found'}
              </AlertTitle>
              {validationResult.valid ? (
                <Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Ready to import:
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip 
                      label={`${validationResult.preview?.categoriesNew?.length || 0} new categories`} 
                      size="small" 
                      color="success"
                    />
                    <Chip 
                      label={`${validationResult.preview?.categoriesExisting?.length || 0} existing categories`} 
                      size="small" 
                      color="info"
                    />
                    <Chip 
                      label={`${validationResult.preview?.totalFaqs || 0} FAQs`} 
                      size="small"
                    />
                  </Stack>
                </Box>
              ) : (
                <List dense>
                  {validationResult.errors?.map((error, index) => (
                    <ListItem key={index} sx={{ py: 0, pl: 0 }}>
                      <ListItemText 
                        primary={`• ${error}`}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Alert>
          )}
        </>
      )}

      {/* Actions */}
      <Stack direction="row" spacing={2} justifyContent="flex-end">
        <CustomButton variant="outlined" onClick={onClose}>
          Cancel
        </CustomButton>
        {importData && !parseError && (
          <>
            {!validationResult?.valid && (
              <CustomButton
                variant="outlined"
                color="info"
                onClick={handleValidate}
                disabled={isValidating}
                startIcon={isValidating ? <CircularProgress size={18} /> : <InfoIcon />}
              >
                {isValidating ? 'Validating...' : 'Validate'}
              </CustomButton>
            )}
            <CustomButton
              variant="contained"
              onClick={handleImport}
              disabled={isImporting || (!validationResult?.valid && !importOptions.updateExisting)}
              startIcon={isImporting ? <CircularProgress size={18} color="inherit" /> : <ImportIcon />}
            >
              {isImporting ? 'Importing...' : 'Import FAQs'}
            </CustomButton>
          </>
        )}
      </Stack>

      {/* Loading Progress */}
      {(isValidating || isImporting) && (
        <LinearProgress sx={{ mt: 2 }} />
      )}
    </Box>
  );
};

// Main Component
const FaqImportExport = ({ open, onClose, onImportSuccess }) => {
  const [activeTab, setActiveTab] = useState(0);

  const handleClose = () => {
    setActiveTab(0);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6">Import / Export FAQs</Typography>
        <IconButton size="small" onClick={handleClose} aria-label="Close dialog">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <Divider />
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, newValue) => setActiveTab(newValue)}
          aria-label="Import Export tabs"
        >
          <Tab 
            icon={<ExportIcon />} 
            iconPosition="start" 
            label="Export" 
            id="faq-import-export-tab-0"
            aria-controls="faq-import-export-tabpanel-0"
          />
          <Tab 
            icon={<ImportIcon />} 
            iconPosition="start" 
            label="Import" 
            id="faq-import-export-tab-1"
            aria-controls="faq-import-export-tabpanel-1"
          />
        </Tabs>
      </Box>
      
      <DialogContent>
        <TabPanel value={activeTab} index={0}>
          <ExportTab onClose={handleClose} />
        </TabPanel>
        <TabPanel value={activeTab} index={1}>
          <ImportTab onClose={handleClose} onSuccess={onImportSuccess} />
        </TabPanel>
      </DialogContent>
    </Dialog>
  );
};

export default FaqImportExport;
